import React from 'react';
import parse, {
  attributesToProps,
  DOMNode,
  Element,
  domToReact,
  HTMLReactParserOptions,
  Text
} from 'html-react-parser';
import {
  ContentComponentProps,
  PageBuilderContentTypes
} from '../../utils/pageBuilder/types';
import { pageBuilderContentTypes } from './ContentTypes';
import { PageBuilderGeneric } from './ContentTypes/Generic';
import { sanitizeHtml } from '../../utils/pageBuilder/sanitizeHtml';
import { InlineStyles } from './InlineStyles';

interface BlockParserArgs {
  html: string;
  contentTypes?: PageBuilderContentTypes;
  additionalProps?: Record<string, unknown>;
  nonce?: string;
}

function isElementNode(node: DOMNode): node is Element {
  return (node as Element).attribs !== undefined;
}

export function parseBlockContent({
  html,
  contentTypes,
  additionalProps,
  nonce
}: BlockParserArgs) {
  const sanitizedHtml = sanitizeHtml(html);
  const mergedContentTypes: PageBuilderContentTypes = {
    ...pageBuilderContentTypes,
    ...contentTypes
  };

  const options: HTMLReactParserOptions = {
    replace: (domNode: DOMNode) => {
      if (!isElementNode(domNode)) {
        return;
      }

      if (domNode.tagName === 'style') {
        const firstChild = domNode.firstChild;
        if (!firstChild || firstChild.type !== 'text') {
          return <></>;
        }

        return (
          <InlineStyles styles={(firstChild as Text).data} nonce={nonce} />
        );
      }

      if (domNode.tagName === 'a') {
        domNode.attribs['data-content-type'] = 'link';
      }

      const { 'data-content-type': contentType, ...attrs } = domNode.attribs;
      if (!contentType) {
        return;
      }

      const props: ContentComponentProps = {
        ...attributesToProps(attrs),
        ...additionalProps,
        htmlTag: domNode.tagName as keyof HTMLElementTagNameMap,
        contentType
      };

      const TypeComponent =
        mergedContentTypes[contentType as keyof PageBuilderContentTypes] ??
        PageBuilderGeneric;

      return (
        <TypeComponent {...props}>
          {domToReact(domNode.children as DOMNode[], options)}
        </TypeComponent>
      );
    }
  };

  return parse(sanitizedHtml, options);
}
